import { BarChart3, Download, UserPlus } from 'lucide-react';
import SalesminerAppLayout from "@/salesminer/layouts/SalesminerAppLayout.tsx";

export default function Intro() {
    return (<SalesminerAppLayout>
            <div className="min-h-screen text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-900">
                {/* Hero Section with Gradient Background */}
                <div className="text-white relative"
                     style={{background: 'linear-gradient(90deg, rgba(6, 243, 225, 1) 0%, rgba(82, 164, 243, 1) 45%, rgba(114, 111, 248, 1) 100%)'}}>
                    <div className="max-w-[1400px] mx-auto px-6 py-20">
                        {/* Corsality Logo - Top Right */}
                        <div className="absolute top-0 right-0 h-full overflow-hidden">
                            <img src="/logo_corsality.svg" alt="Corsality" className="w-96 h-96 opacity-10"/>
                        </div>

                        <div className="text-center">
                            <h1 className="text-white text-5xl md:text-6xl font-bold mb-6">
                                SALESminer
                            </h1>
                            <h2 className="text-white text-xl md:text-2xl font-light leading-relaxed">
                                The world's leading tool to unlock hidden<br/>
                                high value B2B sales opportunities.
                            </h2>
                        </div>
                    </div>
                </div>

                {/* Content Section */}
                <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white">
                    <div className="mx-auto px-6 py-16">
                        {/* Main Description */}
                        <div className="max-w-4xl mx-auto mb-16">
                            <h3 className="text-2xl md:text-3xl font-semibold mb-8 leading-relaxed">
                                We enable you to "look behind the curtain" and identify your key accounts' and
                                prospects' true pain points that your product helps to resolve.
                            </h3>

                            <p className="text-lg text-slate-600 dark:text-white leading-relaxed max-w-4xl mx-auto">
                                Developed with and for Key Account Sellers like yourself, our interface was finetuned to
                                focus on what matters most: to identify the best converting sales opportunities for each
                                of your key accounts.
                            </p>
                        </div>

                        {/* How it Works Section */}
                        <div className="mb-16">
                            <h3 className="text-2xl md:text-3xl font-semibold text-center mb-12">
                                This is how it works
                            </h3>

                            <div className="grid md:grid-cols-3 gap-8">
                                {/* Add your accounts */}
                                <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-8 text-center">
                                    <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                                        <UserPlus className="w-12 h-12 text-slate-600 dark:text-slate-300"/>
                                    </div>
                                    <h4 className="text-xl font-semibold mb-4">Add your accounts</h4>
                                    <p className="leading-relaxed text-slate-600 dark:text-white">
                                        Simply add or import your companies, and dive right into the account.
                                    </p>
                                </div>

                                {/* AI-Powered Analysis */}
                                <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-8 text-center">
                                    <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                                        <BarChart3 className="w-12 h-12 text-slate-600 dark:text-slate-300"/>
                                    </div>
                                    <h4 className="text-xl font-semibold mb-4">AI-Powered Analysis</h4>
                                    <p className="leading-relaxed text-slate-600 dark:text-white">
                                        SALESminer scans billions of data points and metrics to highlight areas your
                                        solution impacts.
                                    </p>
                                </div>

                                {/* Qualify & Export */}
                                <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-8 text-center">
                                    <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                                        <Download className="w-12 h-12 text-slate-600 dark:text-slate-300"/>
                                    </div>
                                    <h4 className="text-xl font-semibold mb-4">Qualify & Export</h4>
                                    <p className="leading-relaxed text-slate-600 dark:text-white">
                                        Browse opportunities, explore findings, and select those most relevant for CRM
                                        export or direct outreach.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Additional Content */}
                        <div className="max-w-4xl mx-auto mb-12">
                            <p className="text-lg text-slate-600 dark:text-white leading-relaxed mb-8">
                                Think of it as AI on steroids but rather than spending multiple days per week on
                                research,
                                you get the deepest possible insights at the click of a button.
                            </p>

                            <p className="text-lg text-slate-600 dark:text-white leading-relaxed mb-12">
                                Just try it and add your first key accounts and explore new opportunities even your best
                                competitors won't be able to find.
                            </p>

                            <div className="text-left max-w-2xl mb-12">
                                <h4 className="text-xl font-semibold mb-6">Understand how</h4>
                                <ul className="space-y-2 font-bold text-slate-900 dark:text-white">
                                    <li className="flex items-start">
                                        <span className="mr-2">•</span>
                                        your solution adds value
                                    </li>
                                    <li className="flex items-start">
                                        <span className="mr-2">•</span>
                                        who your target buyers are
                                    </li>
                                    <li className="flex items-start">
                                        <span className="mr-2">•</span>
                                        how it relates to their strategy or C-level statements
                                    </li>
                                    <li className="flex items-start">
                                        <span className="mr-2">•</span>
                                        and how you can uniquely position your solution as the best option in the
                                        market.
                                    </li>
                                </ul>
                            </div>

                            <div className="space-y-4">
                                <p className="text-2xl text-slate-900 dark:text-white font-semibold">
                                    SALESminer – built on the world's leading engine for deep outside-in corporate
                                    analytics.
                                </p>

                                <p className="text-2xl text-slate-900 dark:text-white font-semibold">
                                    Developed by Sales Pros, for Sales Pros.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </SalesminerAppLayout>
    );
}